/*
 * @prettier
 * @copyright (c) 2019 - present, HGC-AB
 * @licence This source code is licensed under the MIT license described and found in the
 * LICENSE file in the root directory of this source tree.
 */

import React from 'react'
import { graphql } from 'gatsby'

// Styles for rendered markdown
import '../styles/index.css'

// material-ui
import {
  Toolbar,
  Box,
  Typography,
  CardMedia,
  Card,
  Container,
  Divider,
  Fab,
  withStyles,
} from '@material-ui/core'
import KeyboardArrowUpIcon from '@material-ui/icons/KeyboardArrowUp'

// custom components
import ArticleHeader from '../components/Headers/ArticleHeader'
import ScrollTop from '../components/ScrollTop/ScrollTop'
import Footer from '../components/Footer/Footer'
import useSiteMetaData from '../data/useSiteMetaData'

const styles = (theme) => ({
  cardMedia: {
    height: '210px',
  },
  date: {
    paddingTop: theme.spacing(2),
    fontSize: 14,
  },
  author: {
    fontSize: 14,
  },
})

/**
 * Template component to create an article page
 * @param {Object} props - component properties
 * @param {Object} props.classes - override the injected class names
 * @param {Object} props.data - containing the data for an article
 * @param {Object} props.location - representing where the app is from a router perspective
 * @param {Function} props.navigate - provides a means to programmatically navigate
 * @returns {*}
 * @constructor
 */
function ArticlePageTemplate({ classes, data, location, navigate }) {
  const siteMeta = useSiteMetaData()
  const { html, excerpt: description, frontmatter } = data.markdownRemark
  const { title, date, publishedBy } = frontmatter

  return (
    <ArticleHeader
      title={title}
      description={description}
      location={location}
      navigate={navigate}
      siteMeta={siteMeta}
    >
      {/* target for scrollTop component */}
      <Toolbar id="back-to-top-anchor" />
      <Box clone pt={2} pb={2}>
        <Container maxWidth="md">
          <Card elevation={1}>
            <CardMedia className={classes.cardMedia} image="https://source.unsplash.com/random?spring" />
            <Box pt={2} pr={2} pb={2} pl={2}>
              <div dangerouslySetInnerHTML={{ __html: html }} />
              <Box pt={1} pb={1}>
                <Divider variant="fullWidth" />
              </Box>
              <Typography variant="subtitle2" color="textSecondary">
                Published: {date}
              </Typography>
              <Typography variant="subtitle2" color="textSecondary">
                Author: {publishedBy}
              </Typography>
            </Box>
          </Card>
        </Container>
      </Box>
      <Footer siteMeta={siteMeta} />
      <ScrollTop>
        <Fab color="secondary" aria-label="scroll back to top">
          <KeyboardArrowUpIcon />
        </Fab>
      </ScrollTop>
    </ArticleHeader>
  )
}

/**
 * Query article data by slug to inject as props
 */
export const query = graphql`
  query articlePageQuery($slug: String!) {
    markdownRemark(fields: { slug: { eq: $slug } }) {
      frontmatter {
        title
        subtitle
        date(formatString: "YYYY-MM-DD")
        publishedBy
      }
      excerpt(pruneLength: 160)
      timeToRead
      html
    }
  }
`

export default withStyles(styles)(ArticlePageTemplate)
